import React from 'react'

export type ExcelRibbonIconProps = React.SVGProps<SVGSVGElement> & {
  size?: number | string
  strokeWidth?: number | string
}

type IconShellProps = ExcelRibbonIconProps & {
  name: string
  children: React.ReactNode
}

function IconShell({
  name,
  size = 16,
  strokeWidth = 1.8,
  children,
  ...svgProps
}: IconShellProps) {
  return (
    <svg
      {...svgProps}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      data-fg-button-icon
      data-fg-excel-icon={name}
    >
      {children}
    </svg>
  )
}

const iconTextProps = {
  fill: 'currentColor',
  stroke: 'none',
  fontFamily: 'Inter, Segoe UI, Arial, sans-serif',
  fontWeight: 800,
  letterSpacing: 0,
} as const

export function ExcelBorderIcon(props: ExcelRibbonIconProps) {
  return (
    <IconShell name="excel-borders" {...props}>
      <rect x="5" y="5" width="14" height="14" rx="1.5" />
      <path d="M5 12h14M12 5v14" opacity="0.55" />
      <path d="M5 19h14" strokeWidth="2.8" />
    </IconShell>
  )
}

export function ExcelFontColorIcon(props: ExcelRibbonIconProps) {
  return (
    <IconShell name="excel-font-color" {...props}>
      <path d="M8 15l4-9 4 9" />
      <path d="M9.6 12h4.8" />
      <path d="M6 19h12" strokeWidth="2.8" />
    </IconShell>
  )
}

export function ExcelFillColorIcon(props: ExcelRibbonIconProps) {
  return (
    <IconShell name="excel-fill-color" {...props}>
      <path d="M8 6l7 7-4.8 4.8a2.2 2.2 0 0 1-3.1 0L5.2 15.9a2.2 2.2 0 0 1 0-3.1L12 6" />
      <path d="M7.8 10.2h8.1" />
      <path d="M17.5 17.5c.9-1.1 1.6-2.1 2.1-3 .5.9 1.2 1.9 2.1 3" />
      <path d="M4 21h16" strokeWidth="2.4" />
    </IconShell>
  )
}

export function ExcelFontFamilyIcon(props: ExcelRibbonIconProps) {
  return (
    <IconShell name="excel-font-family" {...props}>
      <rect x="4.5" y="5.5" width="15" height="13" rx="1.5" />
      <path d="M8 15.5l4-7 4 7" />
      <path d="M9.6 12.7h4.8" />
      <path d="M6.8 18.4h10.4" opacity="0.35" />
    </IconShell>
  )
}

export function ExcelFontSizeIcon(props: ExcelRibbonIconProps) {
  return (
    <IconShell name="excel-font-size" {...props}>
      <path d="M5 16l3.7-8 3.7 8" />
      <path d="M6.5 13h4.4" />
      <path d="M14.2 10.2h5.6" />
      <path d="M17 7.4v5.6" />
      <text x="17" y="18.2" textAnchor="middle" fontSize="5.6" {...iconTextProps}>pt</text>
    </IconShell>
  )
}

export function ExcelMergeCenterIcon(props: ExcelRibbonIconProps) {
  return (
    <IconShell name="excel-merge-center" {...props}>
      <rect x="4.5" y="6" width="15" height="12" rx="1.5" />
      <path d="M9 6v12M15 6v12" opacity="0.42" />
      <path d="M7.2 12h9.6" />
      <path d="M9.2 9.8L7 12l2.2 2.2M14.8 9.8L17 12l-2.2 2.2" />
    </IconShell>
  )
}

export function ExcelClearFormatsIcon(props: ExcelRibbonIconProps) {
  return (
    <IconShell name="excel-clear-formats" {...props}>
      <path d="M7 16l4.8-10 3.2 6.7" />
      <path d="M8.6 12h5" />
      <path d="M14.5 18.5l4-4a1.9 1.9 0 0 0 0-2.7l-.3-.3a1.9 1.9 0 0 0-2.7 0l-4 4 3 3Z" />
      <path d="M10 20h8" />
    </IconShell>
  )
}

export function ExcelConditionalFormattingIcon(props: ExcelRibbonIconProps) {
  return (
    <IconShell name="excel-conditional-formatting" {...props}>
      <rect x="4.5" y="5" width="15" height="14" rx="1.5" />
      <path d="M4.5 9.7h15M4.5 14.3h15M9.5 5v14M14.5 5v14" opacity="0.45" />
      <rect x="6.3" y="6.7" width="2.1" height="1.6" rx="0.4" fill="currentColor" stroke="none" opacity="0.28" />
      <rect x="11" y="11.2" width="2.1" height="1.6" rx="0.4" fill="currentColor" stroke="none" opacity="0.42" />
      <rect x="15.6" y="15.8" width="2.1" height="1.6" rx="0.4" fill="currentColor" stroke="none" opacity="0.62" />
    </IconShell>
  )
}

export function ExcelCellStylesIcon(props: ExcelRibbonIconProps) {
  return (
    <IconShell name="excel-cell-styles" {...props}>
      <rect x="5" y="5.5" width="14" height="4" rx="1" fill="currentColor" stroke="none" opacity="0.18" />
      <rect x="5" y="10.5" width="14" height="4" rx="1" fill="currentColor" stroke="none" opacity="0.34" />
      <rect x="5" y="15.5" width="14" height="4" rx="1" fill="currentColor" stroke="none" opacity="0.12" />
      <path d="M7 7.5h6M7 12.5h9M7 17.5h5" />
    </IconShell>
  )
}

export function ExcelFormatAsTableIcon(props: ExcelRibbonIconProps) {
  return (
    <IconShell name="excel-format-as-table" {...props}>
      <rect x="4.5" y="5" width="15" height="14" rx="1.5" />
      <rect x="4.5" y="5" width="15" height="4.4" rx="1.5" fill="currentColor" stroke="none" opacity="0.2" />
      <path d="M4.5 9.5h15M4.5 14h15M9.5 5v14M14.5 5v14" />
    </IconShell>
  )
}

export function ExcelNumberFormatIcon(props: ExcelRibbonIconProps) {
  return (
    <IconShell name="excel-number-format" {...props}>
      <rect x="4.5" y="5.5" width="15" height="13" rx="1.5" />
      <text x="12" y="14.9" textAnchor="middle" fontSize="7.2" {...iconTextProps}>123</text>
    </IconShell>
  )
}

export function ExcelAccountingFormatIcon(props: ExcelRibbonIconProps) {
  return (
    <IconShell name="excel-accounting-format" {...props}>
      <rect x="4.5" y="5.5" width="15" height="13" rx="1.5" />
      <text x="9.2" y="15.2" textAnchor="middle" fontSize="10" {...iconTextProps}>$</text>
      <path d="M13 10h3.8M13 14h3.8" />
    </IconShell>
  )
}

export function ExcelPercentStyleIcon(props: ExcelRibbonIconProps) {
  return (
    <IconShell name="excel-percent-style" {...props}>
      <rect x="4.5" y="5.5" width="15" height="13" rx="1.5" />
      <text x="12" y="15.3" textAnchor="middle" fontSize="10" {...iconTextProps}>%</text>
    </IconShell>
  )
}

export function ExcelCommaStyleIcon(props: ExcelRibbonIconProps) {
  return (
    <IconShell name="excel-comma-style" {...props}>
      <rect x="4.5" y="5.5" width="15" height="13" rx="1.5" />
      <path d="M7.5 9.5h7M7.5 13h5.5" />
      <path d="M16.5 13.5c0 1.9-.7 3.3-2.2 4.2" strokeWidth="2.1" />
    </IconShell>
  )
}

export function ExcelIncreaseDecimalIcon(props: ExcelRibbonIconProps) {
  return (
    <IconShell name="excel-increase-decimal" {...props}>
      <text x="7.6" y="15.1" textAnchor="middle" fontSize="8.2" {...iconTextProps}>.0</text>
      <path d="M13 12h6" />
      <path d="M16.5 9.5L19 12l-2.5 2.5" />
      <path d="M5 18.5h14" opacity="0.35" />
    </IconShell>
  )
}

export function ExcelDecreaseDecimalIcon(props: ExcelRibbonIconProps) {
  return (
    <IconShell name="excel-decrease-decimal" {...props}>
      <text x="8.8" y="15.1" textAnchor="middle" fontSize="7.6" {...iconTextProps}>.00</text>
      <path d="M19 12h-6" />
      <path d="M15.5 9.5L13 12l2.5 2.5" />
      <path d="M5 18.5h14" opacity="0.35" />
    </IconShell>
  )
}

export function ExcelInsertCellsIcon(props: ExcelRibbonIconProps) {
  return (
    <IconShell name="excel-insert-cells" {...props}>
      <rect x="4.5" y="5.5" width="12" height="12" rx="1.4" />
      <path d="M4.5 9.5h12M4.5 13.5h12M8.5 5.5v12M12.5 5.5v12" opacity="0.5" />
      <path d="M18 8v6M15 11h6" strokeWidth="2.1" />
    </IconShell>
  )
}

export function ExcelDeleteCellsIcon(props: ExcelRibbonIconProps) {
  return (
    <IconShell name="excel-delete-cells" {...props}>
      <rect x="4.5" y="5.5" width="12" height="12" rx="1.4" />
      <path d="M4.5 9.5h12M4.5 13.5h12M8.5 5.5v12M12.5 5.5v12" opacity="0.5" />
      <path d="M17 9.5l4 4M21 9.5l-4 4" strokeWidth="2.1" />
    </IconShell>
  )
}

export function ExcelClearContentsIcon(props: ExcelRibbonIconProps) {
  return (
    <IconShell name="excel-clear-contents" {...props}>
      <rect x="4.5" y="5.5" width="12" height="12" rx="1.4" />
      <path d="M7 9h7M7 12.5h5" />
      <path d="M16 16l5-5M16 11l5 5" strokeWidth="2.1" />
    </IconShell>
  )
}

export function ExcelFormatCellsIcon(props: ExcelRibbonIconProps) {
  return (
    <IconShell name="excel-format-cells" {...props}>
      <rect x="4.5" y="5.5" width="11" height="11" rx="1.4" />
      <path d="M4.5 9.2h11M8.2 5.5v11M11.9 5.5v11" opacity="0.5" />
      <path d="M17.6 13.5l.5 1.2 1.3.1-.9 1 .2 1.3-1.1-.6-1.1.6.2-1.3-.9-1 1.3-.1.5-1.2Z" fill="currentColor" stroke="none" />
      <path d="M17.6 11.2v1.1M17.6 18.3v1.1M14 15.3l1 .5M20.2 15.8l1-.5M14.8 18.5l.8-.8M19.6 13.9l.8-.8" opacity="0.8" />
    </IconShell>
  )
}
